import { Box, FormControlLabel, Grid } from "@mui/material";
import { useFormik } from "formik";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRadioButton,
  CustomRequiredInputLabel,
} from "../../common/StyledComponents/StyledFields";
import ButtonLoader from "../../common/ButtonLoader/ButtonLoader";
import { useEffect, useState } from "react";
import CustomSelectField from "../../CompactCustomComponents/CustomSelectField";
import CustomDatePickerField from "../../CompactCustomComponents/CustomDatePickerField";
import dayjs from "dayjs";
import { object, string } from "yup";
import { PriestListProps } from "../../../infrastructure/GlobalContextProvider";
import { apiRequest } from "../../../infrastructure/backendService";

const ReportType = {
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
};

const PriestScheduleSummaryIndividual = () => {
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [priestList, setPriestList] = useState<PriestListProps[]>([]);
  const currentDate = new Date();

 
  useEffect(() => {
    apiRequest({
      method: "GET",
      endpoint: "/devotee-management/userCategories/priestAndEventHallList",
      includeToken: true,
    })
      .then((res) => {
        setPriestList(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const monthsList = [
    { id: 1, month: "January" },
    { id: 2, month: "February" },
    { id: 3, month: "March" },
    { id: 4, month: "April" },
    { id: 5, month: "May" },
    { id: 6, month: "June" },
    { id: 7, month: "July" },
    { id: 8, month: "August" },
    { id: 9, month: "September" },
    { id: 10, month: "October" },
    { id: 11, month: "November" },
    { id: 12, month: "December" },
  ];

  const quarterlyYearsList = [
    { quarter: "Jan-March" },
    { quarter: "April-June" },
    { quarter: "July-Sep" },
    { quarter: "Oct-Dec" },
  ];

  const yearsList = [{ year: 2023 }, { year: 2024 }];

  const initialValues = {
    typeOfReport: ReportType.weekly,
    priestName: 0,
    year: currentDate.getFullYear(),
    month: currentDate.getMonth() + 1,
    quarter: 0,
    date: dayjs(currentDate).subtract(1, "day").format("MM-DD-YYYY"),
  };

  const validationSchema = object().shape({
    priestName: string()
      .required("Type of Duty is required")
      .nullable()
      .notOneOf([null, undefined, "", "0"], "Type of Duty is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {},
    enableReinitialize: true,
  });

  const handleChange = (fieldName: string, value: any) => {
    formik.setFieldValue(fieldName, value);
    if (fieldName === "typeOfReport") {
      formik.setTouched({ priestName: false });
      if (value === ReportType.quarterly) {
        formik.setFieldValue("year", 0);
        formik.setFieldValue("quarter", 0);
      } else if (value === ReportType.monthly) {
        formik.setFieldValue("year", currentDate.getFullYear());
        formik.setFieldValue("month", currentDate.getMonth() + 1);
      } else {
        formik.setFieldValue(
          "date",
          dayjs(currentDate).subtract(1, "day").format("MM-DD-YYYY")
        );
      }
    }
  };

  const renderTypeOfReportsRadioButtons = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: { xs: "10px", sm: "16px" },
        }}
      >
        <FormControlLabel
          value={ReportType.weekly}
          control={
            <CustomRadioButton
              checked={formik.values.typeOfReport === ReportType.weekly}
              onChange={(e) => handleChange("typeOfReport", e.target.value)}
              name="typeOfReport"
              sx={{ p: "0px 5px 0 0" }}
            />
          }
          label={ReportType.weekly}
          sx={{
            m: "0px",
            "& .MuiTypography-root": { fontFamily: "Lato", fontSize: "14px" },
          }}
        />
        <FormControlLabel
          value={ReportType.monthly}
          control={
            <CustomRadioButton
              checked={formik.values.typeOfReport === ReportType.monthly}
              onChange={(e) => handleChange("typeOfReport", e.target.value)}
              name="typeOfReport"
              sx={{ p: "0px 5px 0 0" }}
            />
          }
          label={ReportType.monthly}
          sx={{
            m: "0px",
            "& .MuiTypography-root": { fontFamily: "Lato", fontSize: "14px" },
          }}
        />
        <FormControlLabel
          value={ReportType.quarterly}
          control={
            <CustomRadioButton
              checked={formik.values.typeOfReport === ReportType.quarterly}
              onChange={(e) => handleChange("typeOfReport", e.target.value)}
              name="typeOfReport"
              sx={{ p: "0px 5px 0 0" }}
            />
          }
          label={ReportType.quarterly}
          sx={{
            m: "0px",
            "& .MuiTypography-root": { fontFamily: "Lato", fontSize: "14px" },
          }}
        />
      </Box>
    );
  };

  const renderFieldsComponent = () => {
    return (
      <Box sx={{ maxWidth: "400px", mt: "5px" }}>
        {renderTypeOfReportsRadioButtons()}
        <Grid container sx={{ mt: "10px" }} columnSpacing={"12px"}>
          <Grid item xs={12} sm={6} sx={{ maxWidth: "200px" }}>
            {CustomRequiredInputLabel("Type of Duty", false)}
            <CustomSelectField
              handleDropdownChange={handleChange}
              errorMessage={
                formik.touched.priestName ? formik.errors.priestName : ""
              }
              dropDownList={priestList}
              value={formik.values.priestName}
              fieldName={"priestName"}
              placeholder={"Select Priest Name"}
              optionLabel={"priestname"}
              optionValue={"priest_id"}
              overrideStyles={{ background: "white" }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          columnSpacing={"12px"}
          rowSpacing={"10px"}
          sx={{ mt: "0px" }}
        >
          {formik.values.typeOfReport === ReportType.weekly && (
            <Grid item xs={12} sm={6} sx={{ maxWidth: "200px" }}>
              {CustomRequiredInputLabel("Date", false)}
              <CustomDatePickerField
                date={formik.values.date}
                handleDateChange={handleChange}
                errorMessage={undefined}
                name="date"
              />
            </Grid>
          )}
          {formik.values.typeOfReport === ReportType.monthly && (
            <>
              <Grid item xs={12} sm={6} sx={{ maxWidth: "200px" }}>
                {CustomRequiredInputLabel("Month", false)}
                <CustomSelectField
                  handleDropdownChange={handleChange}
                  errorMessage={undefined}
                  dropDownList={monthsList}
                  value={formik.values.month}
                  fieldName={"month"}
                  placeholder={"Select Month"}
                  optionLabel={"month"}
                  optionValue={"id"}
                  overrideStyles={{ background: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ maxWidth: "200px" }}>
                {CustomRequiredInputLabel("Year", false)}
                <CustomSelectField
                  handleDropdownChange={handleChange}
                  errorMessage={undefined}
                  dropDownList={yearsList}
                  value={formik.values.year}
                  fieldName={"year"}
                  placeholder={"Select Year"}
                  optionLabel={"year"}
                  optionValue={"year"}
                  overrideStyles={{ background: "white" }}
                />
              </Grid>
            </>
          )}
          {formik.values.typeOfReport === ReportType.quarterly && (
            <>
              <Grid item xs={12} sm={6} sx={{ maxWidth: "200px" }}>
                {CustomRequiredInputLabel("Search By Quarterly", false)}
                <CustomSelectField
                  handleDropdownChange={handleChange}
                  errorMessage={undefined}
                  dropDownList={quarterlyYearsList}
                  value={formik.values.quarter}
                  fieldName={"quarter"}
                  placeholder={"Select Quarter"}
                  optionLabel={"quarter"}
                  optionValue={"quarter"}
                  overrideStyles={{ background: "white" }}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ maxWidth: "200px" }}>
                {CustomRequiredInputLabel("Year", false)}
                <CustomSelectField
                  handleDropdownChange={handleChange}
                  errorMessage={undefined}
                  dropDownList={yearsList}
                  value={formik.values.year}
                  fieldName={"year"}
                  placeholder={"Select Year"}
                  optionLabel={"year"}
                  optionValue={"year"}
                  overrideStyles={{ background: "white" }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box sx={{ mt: "16px" }}>
        <CommonButton
          sx={{ width: "fit-content", height: "38px", mr: "16px" }}
          onClick={() => {
            if (!isBtnLoading) {
              formik.handleSubmit();
            }
          }}
          id="submit"
        >
          <ButtonLoader isBtnLoading={isBtnLoading} btnText={"Submit"} />
        </CommonButton>
        <CommonOutlinedButton onClick={() => formik.resetForm()}>
          Reset
        </CommonOutlinedButton>
      </Box>
    );
  };
  return (
    <>
      {renderFieldsComponent()}
      {renderButtonsComponent()}
    </>
  );
};

export default PriestScheduleSummaryIndividual;
